import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// ── separate context just for the override — avoids circular dependency ───────
export const AuthOverrideContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [claims,  setClaims]  = useState(null);
  const [loading, setLoading] = useState(true);

  // ── real Firebase auth ─────────────────────────────────────────────────────
  const [realClaims, setRealClaims] = useState(null);
  const [realUser,   setRealUser]   = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const tokenResult = await firebaseUser.getIdTokenResult(true);
          setRealUser(firebaseUser);
          setRealClaims(tokenResult.claims);
          setUser(firebaseUser);
          setClaims(tokenResult.claims);
        } catch (error) {
          console.error('Error getting token:', error);
          setRealUser(null);
          setRealClaims(null);
          setUser(null);
          setClaims(null);
        }
      } else {
        setRealUser(null);
        setRealClaims(null);
        setUser(null);
        setClaims(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // ── called by SuperAdminContext to inject impersonated identity ───────────
  const applyImpersonation = (overrideClaims) => {
    if (!overrideClaims) {
      // restore real claims
      setUser(realUser);
      setClaims(realClaims);
    } else {
      // keep real Firebase user (for auth) but swap claims
      setUser(realUser);
      setClaims(overrideClaims);
    }
  };

  const value = {
    user,
    claims,
    loading,
    realUser,
    realClaims,
    applyImpersonation   // ← exposed so SuperAdminContext can call it
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};